import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, LinearProgress, Tooltip } from "@mui/material";
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Add } from '@mui/icons-material';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import FormikControl from '../../../../../../components/FormikControl/FormikControl';
import { useAlert } from '../../../../../../context/AlertContext';
import { useAppData } from '../../../../../../context/AppDataContext';
import { useKeyPress } from '../../../../../../hooks/useKeyPress';
// Swap this string with your exact inventory services import file path
import inventoryServices from '../inventoryServices'; 
import inventoryTransferServices from '../inventoryTransferServices';

function InventoryTransferForm({ data, callBack }) {
    const { AppAlert } = useAlert();
    const { companyLocationDD } = useAppData(); 
    const [formValues, setFormValues] = useState(null);
    const [dialogStatus, setDialogStatus] = useState(false);
    const [savingInProgress, setSavingStatus] = useState(false);

    const initialValues = {
        transferDate: moment().format(moment.HTML5_FMT.DATE),
        fromLocationId: "",
        toLocationId: "",
        narration: ""
    };

    const updateData = () => {
        let autoFill = {
            id: data.id,
            transferDate: data.transferDate ? data.transferDate : moment().format(moment.HTML5_FMT.DATE),
            fromLocationId: data.fromLocationId,
            toLocationId: data.toLocationId,
            narration: data.narration ? data.narration : ""
        };
        setFormValues(autoFill);
        setDialogStatus(true);
    };

    const validationRule = Yup.object({
        transferDate: Yup.string().required("Required"),
        fromLocationId: Yup.string().required("Source warehouse is required"),
        toLocationId: Yup.string()
            .required("Destination warehouse is required")
            .notOneOf([Yup.ref('fromLocationId')], 'Source and destination cannot be the same warehouse')
    });

    const addNew = () => {
        setFormValues(initialValues);
        setDialogStatus(true);
    };

    const handleClose = () => {
        setDialogStatus(false);
    };

    const navigate = useNavigate();
    const onSubmit = (values, onSubmitProps) => {
        setSavingStatus(true);
        let payload = { ...values };
        inventoryTransferServices.save(payload).then(res => {
            AppAlert(res.msg || "Transfer voucher configuration saved.");
            if (!data) {
                // Instantly pivot down to your line item detail expansion grid page location
                navigate("/inventory/transfer/details/" + res.id);
            }
            setDialogStatus(false);
            if (callBack) {
                callBack();
            }
        }).catch(error => {
            console.log(error);
            AppAlert(error.msg || "Failed to initialize stock transfer record", "error");
        }).finally(() => {
            setSavingStatus(false);
        });
        
    };

    // Binds F5 keyboard interaction to create internal inventory routes instantly
     useKeyPress('F7', addNew, { altKey: true });

    return (
        <>
            {!data &&
                <Tooltip title="Initialize New Stock Transfer Voucher (Alt + F7)">
                    <button type="button" className='btn btn-default w-100 text-start' onClick={addNew}>
                        Transfer Stock
                        <span style={{ fontSize: '0.85em', opacity: 0.7, marginLeft: '8px' }}>
                            (Alt + F7)
                        </span>
                    </button>
                </Tooltip>
            }
            {data &&
                <button className="btn btn-outline-primary me-2" onClick={updateData}>
                    Edit Transfer Voucher Route
                </button>
            }
            <Dialog
                open={dialogStatus}
                keepMounted
                onClose={handleClose}
                fullWidth={true}
                maxWidth="md"
                aria-describedby="stock-transfer-dialog-description"
            >
                <Formik
                    initialValues={formValues || initialValues}
                    validationSchema={validationRule}
                    onSubmit={onSubmit}
                    enableReinitialize 
                    validateOnMount
                >
                    {(formik) => {
                        return (
                            <Form>
                                <DialogTitle className="text-uppercase tracking-wider fw-bold">
                                    {data ? "Modify" : "Initialize"} Stock Transfer
                                </DialogTitle>
                                <DialogContent>
                                    <div className="row ">
                                        <div className='col-md-6'></div>
                                        <div className="col-md-6 my-2">
                                            <FormikControl 
                                                control="input" 
                                                formik={formik} 
                                                type="date" 
                                                label="Transfer Date" 
                                                name="transferDate" 
                                            />
                                        </div>
                                    </div>

                                    <div className="row bg-light p-3 rounded mb-3">
                                        {/* Source Warehouse */}
                                        {dialogStatus &&
                                        <div className="col-md-6 my-2">
                                            <FormikControl 
                                                control="autoselect" 
                                                formik={formik} 
                                                autoFocus
                                                label="Source Warehouse (From Location)"
                                                name="fromLocationId"  
                                                options={companyLocationDD} 
                                                multiple={false} 
                                            />
                                        </div>
                                        }

                                        {/* Destination Warehouse */}
                                        <div className="col-md-6 my-2">
                                            <FormikControl 
                                                control="autoselect" 
                                                formik={formik} 
                                                label="Destination Warehouse (To Location)"
                                                name="toLocationId"  
                                                options={companyLocationDD} 
                                                multiple={false} 
                                            />
                                        </div>
                                    </div>
                                    <div className='row'>
                                        <div className="col-md-12 my-3">
                                            <FormikControl 
                                                control="input" 
                                                formik={formik} 
                                                type="text" 
                                                multiline={true}
                                                rows={3}
                                                label="Narration / Route Remarks" 
                                                name="narration"   
                                            />
                                        </div>
                                    </div>

                                    <div className="row mt-3">
                                        <div className='col-md-12'>
                                            {savingInProgress ?
                                                <LinearProgress />
                                                :
                                                <div className="d-flex justify-content-end gap-2">
                                                    <button className="btn btn-outline-secondary" type="button" onClick={handleClose}>Cancel</button>
                                                    <button 
                                                        className="btn btn-primary" 
                                                        type="submit"
                                                        disabled={!formik.isValid}
                                                    >
                                                        Configure Items Grid &rarr;
                                                    </button>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </DialogContent>
                            </Form>
                        );
                    }}
                </Formik>
            </Dialog>
        </>
    );
}

export default InventoryTransferForm;